import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import { uniqueCaregiverSignalValues } from '@apps/web/components/phoebe_chat_view/tool_calls/caregiver_memory_tool_ui';
import {
    formatLocalShortDateTime,
    formatLocalShortTime,
} from '@apps/web/components/phoebe_chat_view/tool_calls/detail_tool_ui';
import {
    ActionMetricGrid,
    DetailChipList,
    formatCaregiverSignalLabel,
    formatDetailStatus,
    getStringArray,
    ToolDetailHeader,
    ToolDetailKeyValue,
    ToolIssueCard,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { cn } from '@apps/web/lib/utils';

export const RECORD_ACTION_TOOL_NAMES = new Set<string>([
    'update_caregiver_record',
    'update_client_record',
    'update_client_phone_preference',
]);

export const AVAILABILITY_ACTION_TOOL_NAMES = new Set<string>([
    'add_caregiver_weekly_availability',
    'add_caregiver_one_off_availability',
    'update_caregiver_availability',
    'delete_caregiver_availability',
]);

export const MEMORY_ACTION_TOOL_NAMES = new Set<string>([
    'save_caregiver_memory_general',
    'save_caregiver_memory_instruction',
    'save_client_memory',
    'save_client_memory_general',
    'save_client_memory_instruction',
]);

export function SaveNoteWritingIcon({
    className = 'mt-0.5 h-4 w-4',
}: {
    readonly className?: string;
}) {
    return (
        <svg
            className={cn(
                'save-note-writing-icon text-foreground flex-shrink-0',
                className,
            )}
            viewBox="0 0 24 24"
            aria-hidden={true}
            focusable="false"
        >
            <rect
                className="save-note-writing-paper"
                x="4.5"
                y="3.5"
                width="13"
                height="17"
                rx="2.25"
                fill="currentColor"
                fillOpacity="0.04"
                stroke="currentColor"
                strokeOpacity="0.5"
                strokeWidth="1.25"
            />
            <path
                className="save-note-writing-line"
                d="M7.5 9.1C8.6 8.6 9.6 8.6 10.7 9.1C11.8 9.6 12.8 9.6 14 9"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.35"
            />
            <path
                className="save-note-writing-line save-note-writing-line-delay"
                d="M7.5 13.2C8.7 12.7 9.8 12.7 11 13.2C12.1 13.7 13 13.7 14.3 13.1"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.35"
            />
            <g className="save-note-writing-pen">
                <path
                    d="M15.1 4.9L19.1 8.9"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="1.5"
                />
                <path
                    d="M13.3 6.7L15.1 4.9L19.1 8.9L17.3 10.7L13.3 6.7Z"
                    fill="currentColor"
                    fillOpacity="0.08"
                    stroke="currentColor"
                    strokeLinejoin="round"
                    strokeWidth="1.2"
                />
                <path
                    d="M12.5 7.5L13.3 6.7L17.3 10.7L16.5 11.5L12.5 7.5Z"
                    fill="currentColor"
                />
            </g>
        </svg>
    );
}

export function SaveNoteActionCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const hasError = getActionError(data) !== null;

    return (
        <div className="flex items-start gap-1.5">
            <SaveNoteWritingIcon />
            <div className="min-w-0 space-y-0.5">
                <p className="text-foreground text-[11px] font-medium">
                    {hasError ? 'Note save needs review' : 'Phoebe took a note'}
                </p>
                {/* A successful save also renders as a SaveNoteArtifactCard
                    (apps/web/components/phoebe_chat_view/artifacts) with the
                    real content — point to it instead of duplicating the
                    text here, same convention as StructuredReportSummaryCard. */}
                {!hasError ? (
                    <p className="text-muted-foreground text-xs">
                        Content is shown in the saved-note artifact.
                    </p>
                ) : null}
            </div>
        </div>
    );
}

export function getActionError(data: Record<string, unknown>): string | null {
    if (getStringValue(data, 'error')) {
        return getStringValue(data, 'error');
    }
    if (data['success'] === false) {
        return getStringValue(data, 'message') ?? 'Action was not completed.';
    }
    return null;
}

function ActionReviewNeeded({ error }: { readonly error: string | null }) {
    return error ? <ToolIssueCard title="Review needed" error={error} /> : null;
}

const ACTION_ARGUMENT_FIELD_EXCLUSIONS = new Set([
    'availability_id',
    'caregiver_id',
    'client_id',
    'organization_id',
    'recurrence',
]);

export function getActionChangedFields(
    data: Record<string, unknown>,
    args: Record<string, unknown> | null,
): Array<string> {
    const fields = [
        ...getStringArray(data['updated_fields']),
        ...getStringArray(data['changed_fields']),
    ];
    if (fields.length > 0) {
        return uniqueCaregiverSignalValues(fields);
    }

    const patch = isRecord(args?.['patch']) ? args['patch'] : null;
    if (patch) {
        return Object.keys(patch);
    }

    if (!args) {
        return [];
    }

    return Object.keys(args).filter(
        (key) => !ACTION_ARGUMENT_FIELD_EXCLUSIONS.has(key),
    );
}

export function RecordUpdateActionCard({
    toolName,
    data,
    args,
}: {
    readonly toolName: string;
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
}) {
    const { formatName } = useFormatPersonName();
    const error = getActionError(data);
    const isClientTool = toolName.includes('client');
    const isPhonePreferenceTool = toolName === 'update_client_phone_preference';
    const entityLabel = isClientTool ? 'Client' : 'Caregiver';
    const entityName = formatName(
        getStringValue(data, 'client_name') ??
            getStringValue(data, 'caregiver_name') ??
            getStringValue(args, 'client_name') ??
            getStringValue(args, 'caregiver_name'),
    );
    const changedFields = getActionChangedFields(data, args).map(
        formatCaregiverSignalLabel,
    );

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={
                    error
                        ? `${entityLabel} update needs review`
                        : isPhonePreferenceTool
                          ? 'Client phone preference updated'
                          : `${entityLabel} record updated`
                }
                subtitle={entityName}
                badges={
                    error
                        ? [{ label: 'Needs review' }]
                        : [{ label: 'Updated', tone: 'green' }]
                }
            />
            <ActionReviewNeeded error={error} />
            {changedFields.length > 0 ? (
                <ToolTopicSection title="Changed fields">
                    <DetailChipList values={changedFields} />
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

const WEEKDAY_NAMES = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
] as const;

function formatMinuteOfDay(minuteOfDay: number): string {
    const hour24 = Math.floor(minuteOfDay / 60);
    const minute = minuteOfDay % 60;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 || 12;
    return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
}

function formatClockTime(value: string | null): string | null {
    if (!value) {
        return null;
    }
    const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
    if (!match) {
        return value;
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
        return value;
    }
    return formatMinuteOfDay(hour * 60 + minute);
}

function getWeeklyAvailabilityWindow(
    args: Record<string, unknown> | null,
): string | null {
    const day = getStringValue(args, 'day');
    const startTime = formatClockTime(getStringValue(args, 'start_time'));
    const endTime = formatClockTime(getStringValue(args, 'end_time'));
    if (day && startTime && endTime) {
        return `${formatCaregiverSignalLabel(day)} ${startTime} - ${endTime}`;
    }

    const startMinute = getNumberValue(args, 'start_minute_of_week');
    const endMinute = getNumberValue(args, 'end_minute_of_week');
    if (startMinute === null || endMinute === null) {
        return null;
    }
    const dayIndex = Math.floor(startMinute / 1440);
    const dayName = WEEKDAY_NAMES[dayIndex];
    if (!dayName) {
        return null;
    }
    return `${dayName} ${formatMinuteOfDay(startMinute % 1440)} - ${formatMinuteOfDay(endMinute % 1440)}`;
}

function getOneOffAvailabilityWindow(
    args: Record<string, unknown> | null,
): string | null {
    const start =
        getStringValue(args, 'start_time') ?? getStringValue(args, 'start');
    const end = getStringValue(args, 'end_time') ?? getStringValue(args, 'end');
    const startLabel = formatLocalShortDateTime(start);
    const endLabel = formatLocalShortTime(end);
    return startLabel && endLabel ? `${startLabel} - ${endLabel}` : null;
}

export function getAvailabilityWindow(
    toolName: string,
    data: Record<string, unknown>,
    args: Record<string, unknown> | null,
): string | null {
    const explicitWindow =
        getStringValue(data, 'availability') ??
        getStringValue(data, 'window') ??
        getStringValue(data, 'removed_window');
    if (explicitWindow) {
        return explicitWindow;
    }

    if (toolName === 'add_caregiver_one_off_availability') {
        return getOneOffAvailabilityWindow(args);
    }
    return getWeeklyAvailabilityWindow(args);
}

function getAvailabilityActionTitle(
    toolName: string,
    error: string | null,
): string {
    if (error) {
        return 'Availability change needs review';
    }
    if (toolName === 'add_caregiver_weekly_availability') {
        return 'Weekly availability added';
    }
    if (toolName === 'add_caregiver_one_off_availability') {
        return 'One-off availability added';
    }
    if (toolName === 'delete_caregiver_availability') {
        return 'Availability deleted';
    }
    return 'Availability updated';
}

function getActionMetric(
    data: Record<string, unknown>,
    keys: ReadonlyArray<string>,
): number | null {
    for (const key of keys) {
        const value = getNumberValue(data, key);
        if (value !== null) {
            return value;
        }
    }
    return null;
}

export function AvailabilityActionCard({
    toolName,
    data,
    args,
}: {
    readonly toolName: string;
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
}) {
    const { formatName } = useFormatPersonName();
    const error = getActionError(data);
    const status =
        formatDetailStatus(getStringValue(args, 'status')) ??
        formatDetailStatus(getStringValue(data, 'availability_status'));
    const recurrence =
        formatDetailStatus(getStringValue(data, 'recurrence')) ??
        formatDetailStatus(getStringValue(args, 'recurrence'));
    const windowLabel = getAvailabilityWindow(toolName, data, args);
    const availabilityId =
        getStringValue(data, 'availability_id') ??
        getStringValue(args, 'availability_id');
    const caregiverName = formatName(
        getStringValue(data, 'caregiver_name') ??
            getStringValue(args, 'caregiver_name'),
    );

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={getAvailabilityActionTitle(toolName, error)}
                subtitle={caregiverName}
                badges={[
                    ...(error
                        ? [{ label: 'Needs review' }]
                        : [
                              {
                                  label:
                                      toolName ===
                                      'delete_caregiver_availability'
                                          ? 'Removed'
                                          : 'Saved',
                                  tone: 'green' as const,
                              },
                          ]),
                    ...(status
                        ? [
                              {
                                  label: status,
                                  tone:
                                      status.toLowerCase() === 'unavailable'
                                          ? ('red' as const)
                                          : ('green' as const),
                              },
                          ]
                        : []),
                    ...(recurrence
                        ? [{ label: recurrence, tone: 'neutral' as const }]
                        : []),
                ]}
            />
            <ActionReviewNeeded error={error} />
            <ActionMetricGrid
                metrics={[
                    {
                        label: 'Removed rows',
                        value: getActionMetric(data, ['removed_rows']),
                    },
                ]}
            />
            {windowLabel ? (
                <ToolTopicSection title="Window">
                    <p className="text-foreground text-xs">{windowLabel}</p>
                </ToolTopicSection>
            ) : null}
            {availabilityId ? (
                <ToolTopicSection title="Availability ID">
                    <p className="text-muted-foreground truncate font-mono text-[11px]">
                        {availabilityId}
                    </p>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

function getMemoryActionMeta(toolName: string): {
    readonly contentTitle: string;
    readonly title: string;
} {
    const isInstruction = toolName.includes('instruction');
    if (toolName.includes('client')) {
        return {
            contentTitle: isInstruction ? 'Saved instruction' : 'Saved memory',
            title: isInstruction
                ? 'Client instruction saved'
                : 'Client memory saved',
        };
    }
    return {
        contentTitle: isInstruction ? 'Saved instruction' : 'Saved memory',
        title: isInstruction
            ? 'Caregiver instruction saved'
            : 'Caregiver memory saved',
    };
}

function getMemoryPreview(
    toolName: string,
    data: Record<string, unknown>,
    args: Record<string, unknown> | null,
): string | null {
    if (toolName.includes('instruction')) {
        return (
            getStringValue(data, 'instruction_preview') ??
            getStringValue(args, 'instruction')
        );
    }
    return (
        getStringValue(data, 'memory_preview') ??
        getStringValue(args, 'note') ??
        getStringValue(args, 'memory')
    );
}

export function MemoryActionCard({
    toolName,
    data,
    args,
}: {
    readonly toolName: string;
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
}) {
    const { formatName } = useFormatPersonName();
    const error = getActionError(data);
    const meta = getMemoryActionMeta(toolName);
    const preview = getMemoryPreview(toolName, data, args);
    const entityName = formatName(
        getStringValue(data, 'caregiver_name') ??
            getStringValue(data, 'client_name') ??
            getStringValue(args, 'caregiver_name') ??
            getStringValue(args, 'client_name'),
    );
    const memoryId = getStringValue(data, 'memory_id');
    const fragmentId = getStringValue(data, 'fragment_id');

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={error ? 'Memory change needs review' : meta.title}
                subtitle={entityName}
                badges={[
                    error
                        ? { label: 'Needs review' }
                        : {
                              label: 'Saved',
                              tone: 'green' as const,
                          },
                ]}
            />
            <ActionReviewNeeded error={error} />
            {preview ? (
                <ToolTopicSection title={meta.contentTitle}>
                    <p className="text-foreground text-xs">{preview}</p>
                </ToolTopicSection>
            ) : null}
            <ActionMetricGrid
                metrics={[
                    {
                        label: 'Version',
                        value: getActionMetric(data, ['version']),
                    },
                    {
                        label: 'Characters',
                        value: getActionMetric(data, [
                            'character_count',
                            'content_length',
                        ]),
                    },
                ]}
            />
            {memoryId || fragmentId ? (
                <ToolTopicSection title="Memory record">
                    <div className="space-y-1.5">
                        {memoryId ? (
                            <ToolDetailKeyValue
                                label="Memory"
                                value={
                                    <span className="text-muted-foreground font-mono text-[11px]">
                                        {memoryId}
                                    </span>
                                }
                            />
                        ) : null}
                        {fragmentId ? (
                            <ToolDetailKeyValue
                                label="Fragment"
                                value={
                                    <span className="text-muted-foreground font-mono text-[11px]">
                                        {fragmentId}
                                    </span>
                                }
                            />
                        ) : null}
                    </div>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}
